import React, { useState } from 'react';
import {
  keyCompetitors,
  keyCustomers,
  latestInsights,
  keyUpdates,
  favouriteDashboards,
  favouriteSearches,
  focusAreaOptions,
} from '../mockData';
import { useIntel } from '../IntelContext';

function companyInitial(name) {
  if (!name || !String(name).trim()) return '?';
  const c = String(name).trim()[0];
  return /[a-zA-Z0-9]/.test(c) ? c.toUpperCase() : '?';
}

function CompanyAvatar({ company }) {
  const [failed, setFailed] = useState(false);
  const logo = company?.logo;
  const canUseLogo = typeof logo === 'string' && logo.trim().length > 0 && !failed;

  if (canUseLogo) {
    return (
      <img
        src={logo}
        alt={company?.name || 'Company logo'}
        className="company-avatar-img"
        onError={() => setFailed(true)}
      />
    );
  }

  return <>{companyInitial(company?.name)}</>;
}

const CARD_TOAST_LABELS = {
  competitors: `Key Competitors (${keyCompetitors.length})`,
  customers: `Key Customers (${keyCustomers.length})`,
  insights: 'Latest Insights',
  updates: 'Key Updates',
  favDash: 'Favourite Dashboard',
  favSearch: 'Favourite Searches',
};

const HomePage = () => {
  const { toast, navigate } = useIntel();

  const [searchText, setSearchText] = useState('');
  const [focusArea, setFocusArea] = useState('All');
  const [focusOpen, setFocusOpen] = useState(false);

  const [visibilityOpen, setVisibilityOpen] = useState(false);

  const [competitors, setCompetitors] = useState(() =>
    keyCompetitors.map((c) => ({ ...c })),
  );
  const [customers, setCustomers] = useState(() =>
    keyCustomers.map((c) => ({ ...c })),
  );
  const [addCompOpen, setAddCompOpen] = useState(false);
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [newCompName, setNewCompName] = useState('');
  const [newCompType, setNewCompType] = useState('Equipment OEM');
  const [newCustName, setNewCustName] = useState('');
  const [newCustSector, setNewCustSector] = useState('Pharma');

  const [insightDrawer, setInsightDrawer] = useState(null);
  const [updateModalId, setUpdateModalId] = useState(null);

  const filteredInsights =
    focusArea === 'All'
      ? latestInsights
      : latestInsights.filter((i) => i.focusCategory === focusArea);

  const submitSearch = () => {
    navigate('newsfeed', { newsfeedSearch: searchText.trim() });
  };

  const openBragaWithInsight = (insight) => {
    navigate('braga', { bragaQuery: insight.title });
  };

  const addCompetitorSubmit = () => {
    const name = newCompName.trim();
    if (!name) {
      toast('Enter a company name');
      return;
    }
    setCompetitors((prev) => [
      ...prev,
      { id: Date.now(), name, logo: '🏭', color: '#334155' },
    ]);
    setNewCompName('');
    setAddCompOpen(false);
    toast(`Added competitor: ${name} (${newCompType})`);
  };

  const addCustomerSubmit = () => {
    const name = newCustName.trim();
    if (!name) {
      toast('Enter a customer name');
      return;
    }
    setCustomers((prev) => [
      ...prev,
      { id: Date.now(), name, logo: '🏭', color: '#0f766e' },
    ]);
    setNewCustName('');
    setAddCustOpen(false);
    toast(`Added customer: ${name} (${newCustSector})`);
  };

  const updateModal = updateModalId
    ? keyUpdates.find((u) => u.id === updateModalId)
    : null;

  return (
    <div className="homepage">
      <div className="home-toolbar">
        <div className="home-visibility-pills">
          <span className="home-visibility-label">Visibility:</span>
          <button
            type="button"
            className="home-pill"
            onClick={() => setVisibilityOpen(true)}
          >
            Everyone
          </button>
          <button
            type="button"
            className="home-pill home-pill--alt"
            onClick={() => setVisibilityOpen(true)}
          >
            Sales Team
          </button>
        </div>
      </div>

      <div className="search-bar-container">
        <div className="search-bar">
          <span className="search-icon search-icon--svg" aria-hidden="true" />
          <button
            type="button"
            className="voice-icon voice-icon--svg voice-trigger-btn"
            title="Voice input"
            aria-label="Voice input"
            onClick={() => toast('Voice input coming soon')}
          />
          <input
            type="text"
            placeholder="Ask anything"
            className="search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch();
            }}
          />
          <div className="focus-area-dropdown-wrap">
            <button
              type="button"
              className="focus-area-dropdown"
              onClick={() => setFocusOpen((v) => !v)}
              aria-expanded={focusOpen}
            >
              Focus Area ({focusArea === 'All' ? 'All' : focusArea})
              <span className="dropdown-arrow">▼</span>
            </button>
            {focusOpen ? (
              <div className="focus-dropdown-panel">
                {focusAreaOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`gmm-dropdown-item ${focusArea === opt ? 'active' : ''}`}
                    onClick={() => {
                      setFocusArea(opt);
                      setFocusOpen(false);
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="search-submit-btn"
            aria-label="Search"
            onClick={submitSearch}
          >
            →
          </button>
        </div>
      </div>

      {/* Add modals */}
      {addCompOpen ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setAddCompOpen(false)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="gmm-modal-title">Add Key Competitor</h2>
            <label className="gmm-field-label">
              Name
              <input
                className="gmm-input"
                value={newCompName}
                onChange={(e) => setNewCompName(e.target.value)}
                placeholder="Company name"
              />
            </label>
            <label className="gmm-field-label">
              Type
              <select
                className="gmm-input"
                value={newCompType}
                onChange={(e) => setNewCompType(e.target.value)}
              >
                <option value="Equipment OEM">Equipment OEM</option>
                <option value="Service">Service</option>
                <option value="Channel">Channel</option>
              </select>
            </label>
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setAddCompOpen(false)}>
                Cancel
              </button>
              <button type="button" className="gmm-btn-primary" onClick={addCompetitorSubmit}>
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {addCustOpen ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setAddCustOpen(false)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="gmm-modal-title">Add Key Customer</h2>
            <label className="gmm-field-label">
              Name
              <input
                className="gmm-input"
                value={newCustName}
                onChange={(e) => setNewCustName(e.target.value)}
              />
            </label>
            <label className="gmm-field-label">
              Sector
              <select
                className="gmm-input"
                value={newCustSector}
                onChange={(e) => setNewCustSector(e.target.value)}
              >
                <option value="Pharma">Pharma</option>
                <option value="Specialty Chemicals">Specialty Chemicals</option>
                <option value="Crop Science">Crop Science</option>
              </select>
            </label>
            <div className="gmm-modal-actions">
              <button type="button" className="gmm-btn-muted" onClick={() => setAddCustOpen(false)}>
                Cancel
              </button>
              <button type="button" className="gmm-btn-primary" onClick={addCustomerSubmit}>
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {visibilityOpen ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setVisibilityOpen(false)}
        >
          <div className="gmm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="gmm-modal-title">Homepage visibility</h2>
            <p className="gmm-muted">
              Cards respect profile-level scopes. Everyone shows org-wide aggregates; Sales Team trims
              to accounts owned by regional coverage (mock).
            </p>
            <button
              type="button"
              className="gmm-btn-primary"
              onClick={() => setVisibilityOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      {/* Insight drawer */}
      {insightDrawer ? (
        <>
          <div
            role="presentation"
            className="gmm-drawer-scrim fade-in"
            onClick={() => setInsightDrawer(null)}
          />
          <aside className="gmm-drawer-side open">
            <div className="gmm-drawer-side-header">
              <h2>{insightDrawer.title}</h2>
              <button
                type="button"
                className="gmm-drawer-close"
                aria-label="Close"
                onClick={() => setInsightDrawer(null)}
              >
                ×
              </button>
            </div>
            <p className="gmm-muted">
              {insightDrawer.insightCompany} · {insightDrawer.insightDate}
            </p>
            <p className="gmm-drawer-body">{insightDrawer.drawerBody}</p>
            <button
              type="button"
              className="gmm-btn-primary"
              onClick={() => {
                openBragaWithInsight(insightDrawer);
                setInsightDrawer(null);
              }}
            >
              Ask Braga
            </button>
          </aside>
        </>
      ) : null}

      {updateModal ? (
        <div
          className="gmm-modal-backdrop"
          role="presentation"
          onClick={() => setUpdateModalId(null)}
        >
          <div className="gmm-modal gmm-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2 className="gmm-modal-title">{updateModal.title}</h2>
            <p className="gmm-muted">{updateModal.source} · {updateModal.date}</p>
            <div className="gmm-modal-article">{updateModal.articleBody}</div>
            <button
              type="button"
              className="gmm-btn-primary"
              onClick={() => setUpdateModalId(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      <div className="main-cards-grid">
        <div className="card card--top-row card--accent-competitors">
          <div className="card-header">
            <h3>Key Competitors ({competitors.length})</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.competitors} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <button type="button" className="card-add-banner" onClick={() => setAddCompOpen(true)}>
            + Add Key Competitor
          </button>
          <div className="card-body card-body--stretch">
            <div className="companies-grid">
              {competitors.map((competitor) => (
                <div key={competitor.id} className="company-item">
                  <div
                    className="company-avatar"
                    title={competitor.name}
                    aria-hidden="true"
                  >
                    <CompanyAvatar company={competitor} />
                  </div>
                  <div
                    className="company-name company-name--truncate"
                    title={competitor.name}
                  >
                    {competitor.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card card--top-row card--accent-customers">
          <div className="card-header">
            <h3>Key Customers ({customers.length})</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.customers} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <button type="button" className="card-add-banner" onClick={() => setAddCustOpen(true)}>
            + Add Key Customer
          </button>
          <div className="card-body card-body--stretch">
            <div className="companies-grid companies-grid--dense">
              {customers.map((customer) => (
                <div key={customer.id} className="company-item">
                  <div
                    className="company-avatar"
                    title={customer.name}
                    aria-hidden="true"
                  >
                    <CompanyAvatar company={customer} />
                  </div>
                  <div
                    className="company-name company-name--truncate"
                    title={customer.name}
                  >
                    {customer.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card card--top-row card--accent-insights">
          <div className="card-header">
            <h3>Latest Insights</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.insights} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <div className="card-body insights-body card-body--stretch">
            {filteredInsights.length === 0 ? (
              <p className="gmm-muted">No insights for "{focusArea}".</p>
            ) : (
              filteredInsights.map((insight) => (
                <button
                  key={insight.id}
                  type="button"
                  className="insight-item insight-row-btn"
                  onClick={() => setInsightDrawer(insight)}
                >
                  <div className="insight-title">{insight.title}</div>
                  {insight.subtitle && (
                    <div className="insight-subtitle">{insight.subtitle}</div>
                  )}
                  {insight.description && (
                    <div className="insight-description">{insight.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="bottom-cards-grid">
        <div className="card bottom-card card--accent-updates">
          <div className="card-header">
            <h3>Key Updates</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.updates} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <div className="card-body card-body--bottom">
            {keyUpdates.map((update) => (
              <button
                key={update.id}
                type="button"
                className="update-item update-row-btn"
                onClick={() => setUpdateModalId(update.id)}
              >
                <div
                  className="update-avatar"
                  title={update.source}
                  aria-hidden="true"
                >
                  {companyInitial(update.source)}
                </div>
                <div className="update-content">
                  <div className="update-title">{update.title}</div>
                  <div className="update-meta">
                    {update.source} · {update.date}
                  </div>
                </div>
              </button>
            ))}
            <p className="card-body-placeholder">
              Additional filings and alerts surface here as they are detected.
            </p>
          </div>
        </div>

        <div className="card bottom-card card--accent-favourite-dashboard">
          <div className="card-header">
            <h3>Favourite Dashboard</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.favDash} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <div className="card-body card-body--bottom">
            {favouriteDashboards.map((dashboard) => (
              <button
                key={dashboard.id}
                type="button"
                className="favourite-item favourite-click"
                onClick={() =>
                  navigate('dashboard', { dashboardWorkspaceId: dashboard.workspaceId })
                }
              >
                <div className="favourite-title">{dashboard.title}</div>
                <div className="favourite-subtitle">{dashboard.workspace}</div>
              </button>
            ))}
            <p className="card-body-placeholder">
              Pin dashboards from your workspaces to open them in one click.
            </p>
          </div>
        </div>

        <div className="card bottom-card card--accent-favourite-searches">
          <div className="card-header">
            <h3>Favourite Searches</h3>
            <button
              type="button"
              className="card-arrow-btn"
              aria-label="Expand"
              onClick={() =>
                toast(`${CARD_TOAST_LABELS.favSearch} — Full view coming soon`)
              }
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>
          <div className="card-body card-body--bottom">
            {favouriteSearches.map((search) => (
              <button
                key={search.id}
                type="button"
                className="favourite-search-item favourite-click"
                onClick={() =>
                  navigate('newsfeed', { newsfeedSearch: search.title })
                }
              >
                {search.title}
              </button>
            ))}
            <p className="card-body-placeholder">
              Saved queries appear here for quick re-runs and alerts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
